import React, { useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { contact } from '@/data/contact';

const fieldClass =
  'bg-white border-gray-300 text-gray-900 placeholder:text-gray-500 focus:border-[#398024] focus-visible:ring-[#398024]';

const ContactForm = ({
  variant = 'contact',
  idPrefix = 'contact',
  submitLabel = 'Send Message',
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');

  const isCareers = variant === 'careers';

  const placeholders = useMemo(
    () =>
      isCareers
        ? {
            message: 'Tell us about your background and the role you are interested in.',
          }
        : {
            message: 'Tell us about your project or enquiry…',
          },
    [isCareers]
  );

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = 'Please enter your name.';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = 'Please enter a valid email address.';
    }
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      next.message = 'Please enter a message of at least 10 characters.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!validate()) {
      setStatus('error');
      return;
    }

    const subject = isCareers
      ? `Career enquiry from ${formData.name}`
      : `Website enquiry from ${formData.name}`;
    const body = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      formData.phone ? `Phone: ${formData.phone}` : null,
      formData.company ? `Company: ${formData.company}` : null,
      '',
      formData.message,
    ]
      .filter(Boolean)
      .join('\n');

    window.location.href = `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setStatus('success');
    setFormData({ name: '', email: '', phone: '', company: '', message: '' });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" noValidate>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor={`${idPrefix}-name`} className="block text-gray-700 text-sm font-medium mb-2">
            Full Name *
          </label>
          <Input
            type="text"
            id={`${idPrefix}-name`}
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            autoComplete="name"
            className={fieldClass}
            placeholder="Your name"
            aria-invalid={Boolean(errors.name)}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={`${idPrefix}-email`} className="block text-gray-700 text-sm font-medium mb-2">
            Email Address *
          </label>
          <Input
            type="email"
            id={`${idPrefix}-email`}
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            autoComplete="email"
            className={fieldClass}
            placeholder="you@company.com"
            aria-invalid={Boolean(errors.email)}
          />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor={`${idPrefix}-phone`} className="block text-gray-700 text-sm font-medium mb-2">
            Phone Number
          </label>
          <Input
            type="tel"
            id={`${idPrefix}-phone`}
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            autoComplete="tel"
            className={fieldClass}
            placeholder="+962 …"
          />
        </div>
        <div>
          <label htmlFor={`${idPrefix}-company`} className="block text-gray-700 text-sm font-medium mb-2">
            {isCareers ? 'Current role or discipline' : 'Company Name'}
          </label>
          <Input
            type="text"
            id={`${idPrefix}-company`}
            name="company"
            value={formData.company}
            onChange={handleChange}
            className={fieldClass}
            placeholder={isCareers ? 'Engineer, technician, …' : 'Your organisation'}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${idPrefix}-message`} className="block text-gray-700 text-sm font-medium mb-2">
          Message *
        </label>
        <Textarea
          id={`${idPrefix}-message`}
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={6}
          className={fieldClass}
          placeholder={placeholders.message}
          aria-invalid={Boolean(errors.message)}
        />
        {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
      </div>

      {status === 'success' && (
        <p className="text-sm text-[#398024] font-medium" role="status">
          Your email client should open with the message ready to send to {contact.email}. If it does not, please email us directly.
        </p>
      )}
      {status === 'error' && (
        <p className="text-sm text-red-600" role="alert">
          Please complete the required fields and try again.
        </p>
      )}

      <Button
        type="submit"
        className="w-full bg-[#398024] text-white py-6 text-lg font-semibold hover:bg-[#2d6b1a] hover:shadow-xl transition-all duration-300"
      >
        <span>{submitLabel}</span>
        <Send size={20} className="ml-2" />
      </Button>
    </form>
  );
};

export default ContactForm;
